import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Modal } from '/imports/ui/Modal';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { NumberInput } from '/imports/ui/NumberInput';
import { Checkbox } from '/imports/ui/Checkbox';
import { Select } from '/imports/ui/Select';
import { bulkAddSellTargets, BulkAddSellTargetsConfig } from '/imports/api/sellTargets';

type Selector = 'id' | 'name' | 'set' | 'all';

const selectorOptions = [
  { value: 'id', label: 'Single card by ID' },
  { value: 'name', label: 'Cards by name' },
  { value: 'set', label: 'All cards in a set' },
  { value: 'all', label: 'All cards (with prices)' },
];

const isfoilOptions = [
  { value: '', label: 'Any' },
  { value: 'True', label: 'Foil only' },
  { value: 'False', label: 'Non-foil only' },
];

const rarityOptions = [
  { value: '', label: 'Any' },
  { value: 'COMMON', label: 'Common' },
  { value: 'UNCOMMON', label: 'Uncommon' },
  { value: 'RARE', label: 'Rare' },
  { value: 'MYTHIC', label: 'Mythic' },
  { value: 'BASIC_LAND', label: 'Basic land' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'BONUS', label: 'Bonus' },
  { value: 'TOKEN', label: 'Token' },
];

const pricekeyOptions = [
  { value: 'manSellPrice', label: 'manSellPrice' },
  { value: 'gbSellPrice', label: 'gbSellPrice' },
  { value: 'manBuyPrice', label: 'manBuyPrice' },
  { value: 'gbBuyPrice', label: 'gbBuyPrice' },
];

// Tailwind port of the old `Form` styled.div. Descendant rules (.row, label,
// .hint) become arbitrary variants on the wrapper to faithfully match.
const formClasses = [
  'flex flex-col gap-3 min-w-[30rem]',
  '[&_.row]:flex [&_.row]:gap-3',
  '[&_.row>*]:flex-1',
  '[&_label]:block [&_label]:text-[0.85rem] [&_label]:font-semibold [&_label]:mb-1',
  '[&_.hint]:text-xs [&_.hint]:text-fg-3 [&_.hint]:mt-1',
].join(' ');

interface Props { onClose(): void }

export const SellTargetsBulkModal = ({ onClose }: Props) => {
  const [selector, setSelector] = useState<Selector>('all');
  const [id, setId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [cardSet, setCardSet] = useState<string>('');
  const [foil, setFoil] = useState<boolean>(false);
  const [rarity, setRarity] = useState<string>('');
  const [isfoil, setIsfoil] = useState<string>('');
  const [minprice, setMinprice] = useState<string>('');
  const [maxprice, setMaxprice] = useState<string>('');
  const [pricekey, setPricekey] = useState<string>('manSellPrice');
  const [withprices, setWithprices] = useState<boolean>(false);
  const [sellcap, setSellcap] = useState<string>('');
  const [sellcapMin, setSellcapMin] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);

  const submit = async () => {
    const cfg: BulkAddSellTargetsConfig = {};
    if (selector === 'id') {
      const n = Number(id);
      if (!Number.isFinite(n)) return toast.error('Card ID must be a number');
      cfg.id = n;
    }
    else if (selector === 'name') {
      if (!name.trim()) return toast.error('Name is required');
      cfg.name = name.trim();
      if (cardSet.trim()) cfg.set = cardSet.trim();
      cfg.foil = foil;
    }
    else if (selector === 'set') {
      if (!cardSet.trim()) return toast.error('Set is required');
      cfg.set = cardSet.trim();
    }
    else {
      cfg.all = true;
    }

    if (selector !== 'id') {
      if (rarity) cfg.rarity = rarity;
      if (isfoil === 'True' || isfoil === 'False') cfg.isfoil = isfoil;
      if (minprice !== '' && Number.isFinite(Number(minprice))) cfg.minprice = Number(minprice);
      if (maxprice !== '' && Number.isFinite(Number(maxprice))) cfg.maxprice = Number(maxprice);
      if (pricekey) cfg.pricekey = pricekey;
      if (withprices) cfg.withprices = true;
    }

    if (sellcap !== '' && Number.isFinite(Number(sellcap))) cfg.sellcap = Number(sellcap);
    if (sellcapMin !== '' && Number.isFinite(Number(sellcapMin))) cfg.sellcapMin = Number(sellcapMin);

    setBusy(true);
    try {
      const res = await bulkAddSellTargets.call(cfg);
      toast.success(`Added ${res.added} sell-target${res.added === 1 ? '' : 's'}` + (res.skipped ? `, skipped ${res.skipped}` : ''));
      if (res.errors.length) for (const e of res.errors) toast.error(e);
      onClose();
    }
    catch (err: any) {
      toast.error(`${err}`);
    }
    finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Bulk-add sell targets"
      onClose={busy ? undefined : onClose}
      buttons={
        <>
          <Button className="btn primary" onClick={submit} disabled={busy}>
            {busy ? 'Adding…' : 'Add Sell Targets'}
          </Button>
          <Button className="btn secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        </>
      }
    >
      <div className={formClasses}>
        <div>
          <label>Selector</label>
          <Select value={selector} options={selectorOptions} onChange={(v) => setSelector(v as Selector)} />
        </div>

        {selector === 'id' && (
          <div>
            <label>Card ID</label>
            <Input value={id} onChange={(v) => setId(v || '')} placeholder="e.g. 12336" />
          </div>
        )}

        {selector === 'name' && (
          <>
            <div>
              <label>Card name</label>
              <Input value={name} onChange={(v) => setName(v || '')} placeholder="e.g. Exploration" />
            </div>
            <div className="row">
              <div>
                <label>Set (optional)</label>
                <Input value={cardSet} onChange={(v) => setCardSet(v || '')} placeholder="e.g. UZ" />
              </div>
              <div>
                <label>Foil</label>
                <Checkbox checked={foil} onChange={(v) => setFoil(!!v)}>Foil version</Checkbox>
              </div>
            </div>
          </>
        )}

        {selector === 'set' && (
          <div>
            <label>Set name(s) — comma-separated for multiple</label>
            <Input value={cardSet} onChange={(v) => setCardSet(v || '')} placeholder="e.g. SOS,TMT,DFT" />
          </div>
        )}

        {selector !== 'id' && (
          <>
            <div className="row">
              <div>
                <label>Rarity filter</label>
                <Select value={rarity} options={rarityOptions} onChange={(v) => setRarity(v || '')} />
              </div>
              <div>
                <label>Foil filter</label>
                <Select value={isfoil} options={isfoilOptions} onChange={(v) => setIsfoil(v || '')} />
              </div>
            </div>
            <div className="row">
              <div>
                <label>Min price</label>
                <Input value={minprice} onChange={(v) => setMinprice(v || '')} placeholder="(none)" />
              </div>
              <div>
                <label>Max price</label>
                <Input value={maxprice} onChange={(v) => setMaxprice(v || '')} placeholder="(none)" />
              </div>
              <div>
                <label>Price key</label>
                <Select value={pricekey} options={pricekeyOptions} onChange={(v) => setPricekey(v || 'manSellPrice')} />
              </div>
            </div>
            {selector !== 'all' && (
              <div>
                <Checkbox checked={withprices} onChange={(v) => setWithprices(!!v)}>Only cards that have at least one price</Checkbox>
              </div>
            )}
          </>
        )}

        <div className="row">
          <div>
            <label>Sell cap (optional, overrides bot.stocksize)</label>
            <NumberInput value={Number(sellcap) || 0} onChange={(v) => setSellcap(String(v ?? ''))} />
          </div>
          <div>
            <label>Min sell price (optional)</label>
            <Input value={sellcapMin} onChange={(v) => setSellcapMin(v || '')} placeholder="(none)" />
          </div>
        </div>
      </div>
    </Modal>
  );
};
